import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDogs } from "@/context/DogsContext";
import { useAuth } from "@/context/AuthContext";
import { useWelcomeToast } from "@/lib/useWelcomeToast";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatCard, SectionTitle, StreakCard } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import BreedingHistoryCard from "@/components/app/BreedingHistoryCard";
import { showsWarmupReminder, showsSeniorReminder, showsPuppyReminder, DOG_TYPES, getPromptsConfig } from "@/lib/dogTypes";
import { MAX_PHOTO_BYTES } from "@/lib/constants";
import { POPULAR_BREEDS } from "@/lib/breeds";
import { toast } from "sonner";
import { Calendar, HeartPulse, Stamp, NotebookText, Footprints, Heart, Baby, Trash2, ChevronDown, Pencil } from "lucide-react";
import { format, differenceInYears, differenceInDays, parseISO } from "date-fns";
import AddDogDialog from "@/components/app/AddDogDialog";

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function OverviewPage() {
  const { activeDog, refresh, selectDog } = useDogs();
  const { user, refresh: refreshAuth } = useAuth();
  const [eventsCount, setEventsCount] = useState(0);
  const [resultsCount, setResultsCount] = useState(0);
  const [titlesCount, setTitlesCount] = useState(0);
  const [streaks, setStreaks] = useState(null);
  const [profileForm, setProfileForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingDog, setDeletingDog] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const today = useMemo(() => new Date(), []);

  // When a breeder-mode dog becomes active but the cached user record still
  // shows is_breeder=false (e.g. user just saved the dog), pull a fresh
  // session so the welcome toast (and the breeder UX gates) light up
  // without a hard reload. Once is_breeder flips true the guard turns
  // false, so this is idempotent.
  const isBreederFlag = !!user?.is_breeder;
  useEffect(() => {
    if (activeDog?.dog_type === "breeder" && user && !isBreederFlag) {
      refreshAuth();
    }
  }, [activeDog?.dog_type, isBreederFlag, user, refreshAuth]);

  // First-visit welcome toasts. Each lights up the matching sidebar
  // affordances on the user's first interaction with that mode. Independent
  // localStorage gates so they coexist; see /app/frontend/src/lib/useWelcomeToast.js.
  useWelcomeToast({
    shouldFire: !!user?.is_breeder,
    storageKey: "ydd_breeder_welcome_seen_v1",
    toastId: "breeder-welcome",
    copy: "Welcome, breeder! Heat cycles, Gestation, and Litters are now in your sidebar →",
  });
  useWelcomeToast({
    shouldFire: activeDog?.dog_type === "show",
    storageKey: "ydd_show_welcome_seen_v1",
    toastId: "show-welcome",
    copy: "Welcome to Show mode! Show plans, results & titles are now in your sidebar →",
  });
  useWelcomeToast({
    shouldFire: activeDog?.dog_type === "sport",
    storageKey: "ydd_sport_welcome_seen_v1",
    toastId: "sport-welcome",
    copy: "Welcome to Sport mode! Show plans, results & titles are now in your sidebar →",
  });
  useWelcomeToast({
    shouldFire: activeDog?.dog_type === "puppy",
    storageKey: "ydd_puppy_welcome_seen_v1",
    toastId: "puppy-welcome",
    copy: "Welcome to Puppy mode! Growth tracking, socialization & milestones are on Your Dogs →",
  });
  useWelcomeToast({
    shouldFire: activeDog?.dog_type === "senior",
    storageKey: "ydd_senior_welcome_seen_v1",
    toastId: "senior-welcome",
    copy: "Welcome to Senior mode! Comfort, mobility & gentle-care prompts are on Your Dogs →",
  });

  useEffect(() => {
    if (!activeDog) {
      setProfileForm(null);
      setStreaks(null);
      return;
    }
    setProfileForm({ ...activeDog });
    (async () => {
      const [e, r, t, s] = await Promise.all([
        api.get("/events", { params: { dog_id: activeDog.id, year: today.getFullYear(), month: today.getMonth() + 1 } }),
        api.get("/show-results", { params: { dog_id: activeDog.id } }),
        api.get("/titles", { params: { dog_id: activeDog.id } }).catch(() => ({ data: { items: [] } })),
        api.get(`/dogs/${activeDog.id}/streaks`).catch(() => ({ data: null })),
      ]);
      setEventsCount((e.data.items || []).length);
      setResultsCount((r.data.items || []).length);
      setTitlesCount((t.data.items || []).length);
      setStreaks(s.data);
    })();
  }, [activeDog, today]);

  const setP = (k, v) => setProfileForm((f) => ({ ...f, [k]: v }));

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { ...profileForm };
      delete payload.id; delete payload.owner_id; delete payload.shared;
      delete payload.share_permission; delete payload.created_at; delete payload.updated_at;
      await api.put(`/dogs/${activeDog.id}`, payload);
      toast.success("Profile saved");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const removeDog = async () => {
    if (!window.confirm(`Delete ${activeDog.name}? This removes all related data.`)) return;
    const deletedName = activeDog.name;
    setDeletingDog(true);
    try {
      const { data } = await api.delete(`/dogs/${activeDog.id}`);
      const trashId = data?.trash_id;
      await refresh();
      // 10-second Undo affordance via sonner. The backend keeps the snapshot
      // for an hour but we don't want the toast hanging around forever.
      toast.success(`${deletedName} removed`, {
        duration: 10000,
        action: trashId
          ? {
              label: "Undo",
              onClick: async () => {
                try {
                  const r = await api.post(`/dogs/_trash/${trashId}/restore`);
                  await refresh();
                  selectDog?.(r.data.dog_id);
                  toast.success(`${deletedName} restored`);
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not restore");
                }
              },
            }
          : undefined,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeletingDog(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Choose a photo under 2 MB");
      return;
    }
    setP("photo_url", await fileToDataUrl(file));
  };

  const age = activeDog?.birthday ? differenceInYears(today, parseISO(activeDog.birthday)) : null;
  const nextBirthday = activeDog?.birthday
    ? (() => {
        const b = parseISO(activeDog.birthday);
        const next = new Date(today.getFullYear(), b.getMonth(), b.getDate());
        if (next < today) next.setFullYear(today.getFullYear() + 1);
        return differenceInDays(next, today);
      })()
    : null;

  return (
    <EmptyDogState>
      {activeDog && profileForm && (
        <div className="space-y-10" data-testid="overview-page">
          {/* New Year banner */}
          {today.getMonth() === 0 ? (
            <Card className="border-border shadow-none rounded-lg bg-secondary" data-testid="new-year-banner">
              <CardContent className="p-5 sm:p-6 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <NotebookText size={18} />
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold tracking-tight">Happy new year — your {today.getFullYear() - 1} year-in-review is ready</p>
                    <p className="text-sm text-muted-foreground">Cumulative stats: show points, new titles, shows attended, training, exercise & webinars.</p>
                  </div>
                </div>
                <a href="/year-in-review" className="text-sm font-medium underline underline-offset-4" data-testid="open-year-review">
                  Open year in review →
                </a>
              </CardContent>
            </Card>
          ) : null}

          {/* Hero */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <Card className="lg:col-span-2 border-border shadow-none rounded-lg overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="aspect-square sm:aspect-auto sm:h-full bg-muted">
                    {activeDog.photo_url ? (
                      <img src={activeDog.photo_url} alt={activeDog.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No photo yet</div>
                    )}
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Active Dog</p>
                    <div className="flex items-start justify-between gap-3 mt-2">
                      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter">{activeDog.name}</h1>
                      <button
                        type="button"
                        onClick={() => setEditOpen(true)}
                        className="shrink-0 mt-2 h-9 w-9 rounded-md border border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
                        aria-label={`Edit ${activeDog.name}`}
                        title="Edit basics (name, breed, AKC #, microchip, photo)"
                        data-testid="overview-edit-dog"
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                    <p className="text-muted-foreground mt-2">{activeDog.breed || "Breed not set"}</p>
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AKC#, UKC#, IFC#, Other</p>
                        <p className="text-sm font-medium">{activeDog.akc_number || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Microchip</p>
                        <p className="text-sm font-medium">{activeDog.microchip || "—"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
              <StreakCard streaks={streaks} testid="stat-streak" />
              <StatCard testid="stat-age" label="Age" value={age != null ? `${age}y` : "—"} hint={activeDog.birthday || "No birthday set"} />
              <StatCard
                testid="stat-next-bday"
                label="Next Birthday"
                value={nextBirthday != null ? `${nextBirthday}d` : "—"}
                hint="Days away"
              />
              <StatCard testid="stat-events" label="Events this month" value={eventsCount} hint="grooming + training" />
              <StatCard testid="stat-results" label="Show results" value={resultsCount} hint="lifetime" />
              <StatCard testid="stat-titles" label="Titles earned" value={titlesCount} hint="lifetime" />
            </div>
          </div>

          {/* Rainbow Bridge memorial strip — appears just under the hero for
              dogs the owner has lost, with passing date + a spot to keep a
              favorite memory inline. Fully editable so the memory can grow
              over time. Suppressed entirely when the user has opted out of
              Rainbow Bridge components in Settings. */}
          {activeDog.rainbow_bridge && !user?.rainbow_bridge_hidden ? (
            <RainbowBridgeStrip
              dog={activeDog}
              onSaved={refresh}
            />
          ) : null}

          {/* Breeding history — heat cycles + litters linked to this dog as
              dam. Hidden entirely for males/non-breeders with no records; a
              female breeder dog with no records sees an empty-state CTA. */}
          <BreedingHistoryCard dog={activeDog} isBreeder={!!user?.is_breeder} />

          {/* Puppy-mode quick link — mirrors the sidebar Puppy Log entry so
              new-puppy owners see a "Log today" affordance without hunting
              the sidebar. Only rendered when the active dog is in puppy mode. */}
          {activeDog?.dog_type === "puppy" ? (
            <Card className="border-border shadow-none rounded-lg" data-testid="overview-puppy-log-card">
              <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium">Daily Puppy Log</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Track demeanor, meals, potties, naps, socialization, grooming, table-training, and stacking.
                  </p>
                </div>
                <Link to="/puppy-log">
                  <Button data-testid="overview-puppy-log-cta">Log today →</Button>
                </Link>
              </CardContent>
            </Card>
          ) : null}

          {/* Warm-up reminder for all dogs */}
          {showsWarmupReminder(activeDog?.dog_type) ? (
            <Card className="border-border shadow-none rounded-lg bg-secondary" data-testid="warmup-reminder">
              <CardContent className="p-5 flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <Footprints size={18} />
                </div>
                <div>
                  <p className="font-medium">Warm up first</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Always start any exercise session with a 10 or 15 minute walk to loosen muscles and protect joints.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Puppy reminder */}
          {showsPuppyReminder(activeDog?.dog_type) ? (
            <Card className="border-border shadow-none rounded-lg bg-accent" data-testid="puppy-reminder">
              <CardContent className="p-5 flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <Baby size={18} />
                </div>
                <div>
                  <p className="font-medium">Growing puppy</p>
                  <p className="text-sm text-foreground/80 mt-0.5">
                    Keep training sessions short (5–10 min, several times a day) and avoid long
                    forced exercise — growing joints need free play, not endurance work. Use the
                    5-minute-per-month-of-age rule for structured walks.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Senior dog reminder */}
          {showsSeniorReminder(activeDog?.dog_type) ? (
            <Card className="border-border shadow-none rounded-lg bg-accent" data-testid="senior-reminder">
              <CardContent className="p-5 flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <Heart size={18} />
                </div>
                <div>
                  <p className="font-medium">Senior care</p>
                  <p className="text-sm text-foreground/80 mt-0.5">
                    Senior dogs need regular, gentle exercise. Aim for short, consistent sessions instead of long, hard ones — and watch for stiffness, fatigue, or hesitation.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Today's care reminders — read-only dropdown. Track them on the Weekly Planner. */}
          <TodayRemindersDropdown dogType={activeDog?.dog_type} />

          {/* ----------------- 3-Step Setup Wizard ----------------- */}
          <SetupWizard
            profileForm={profileForm}
            setP={setP}
            onPhoto={onPhoto}
            saveProfile={saveProfile}
            removeDog={removeDog}
            savingProfile={savingProfile}
            deletingDog={deletingDog}
            isShared={!!activeDog.shared}
            activeDog={activeDog}
          />

          {/* Quick nav row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: HeartPulse, label: "Weight & Food", to: "/vet-and-health" },
              { icon: Calendar, label: "Calendar & Journal", to: "/weekly" },
              { icon: Stamp, label: "Show Plans", to: "/show-plans" },
              { icon: NotebookText, label: "Year in Review", to: "/year-in-review" },
            ].map((q) => (
              <a key={q.label} href={q.to}
                 className="border border-border rounded-lg p-5 bg-card hover:-translate-y-0.5 hover:border-primary transition-all"
                 data-testid={`quick-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <q.icon size={20} className="text-primary mb-3" />
                <p className="font-medium text-sm">{q.label}</p>
              </a>
            ))}
          </div>
        </div>
      )}
      {activeDog && (
        <AddDogDialog
          key={activeDog.id /* fresh state per dog when re-opened */}
          open={editOpen}
          onOpenChange={setEditOpen}
          editDog={activeDog}
        />
      )}
    </EmptyDogState>
  );
}

function TodayRemindersDropdown({ dogType }) {
  const [open, setOpen] = useState(true);
  const cfg = getPromptsConfig(dogType);
  const groups = [
    { title: "Daily", items: cfg.daily },
    { title: "Weekly", items: cfg.weekly },
    { title: "Monthly", items: cfg.monthly },
  ].filter((g) => g.items && g.items.length > 0);
  if (groups.length === 0) return null;

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="today-reminders-card">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 p-5 sm:p-6 text-left hover:bg-muted/40 transition-colors"
          data-testid="today-reminders-toggle"
          aria-expanded={open}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <NotebookText size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Today&apos;s care reminders</p>
              <p className="font-medium text-base">Daily, weekly &amp; monthly checklist</p>
              <p className="text-xs text-muted-foreground mt-0.5">Read-only — track these on the Weekly Planner.</p>
            </div>
          </div>
          <ChevronDown
            size={18}
            className={`shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open ? (
          <div className="px-5 sm:px-6 pb-6 pt-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="today-reminders-list">
            {groups.map((g) => (
              <div key={g.title}>
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{g.title}</p>
                <ul className="space-y-1.5">
                  {g.items.map((p) => (
                    <li
                      key={p.key}
                      className="text-sm text-foreground/90 bg-secondary/40 rounded-md px-3 py-1.5"
                      data-testid={`reminder-${p.key}`}
                    >
                      {p.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SetupWizard({ profileForm, setP, onPhoto, saveProfile, removeDog, savingProfile, deletingDog, isShared, activeDog }) {
  const [step, setStep] = useState(profileForm?.dog_type ? 2 : 1);

  const steps = [
    { n: 1, label: "Dog type" },
    { n: 2, label: "Dog details" },
    { n: 3, label: "Pedigree" },
  ];

  const canNext = step === 1 ? !!profileForm.dog_type : step === 2 ? !!(profileForm.name && profileForm.name.trim()) : true;

  return (
    <div className="pt-4" data-testid="setup-wizard">
      <SectionTitle overline={`Setup · Step ${step} of 3`} title={steps[step - 1].label} action={
        <div className="flex gap-2">
          {!isShared && (
            <Button variant="outline" onClick={removeDog} disabled={deletingDog} data-testid="delete-dog-button">
              <Trash2 size={16} className="mr-1.5" /> {deletingDog ? "Removing…" : "Remove"}
            </Button>
          )}
        </div>
      } />

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6" data-testid="wizard-stepper">
        {steps.map((s, i) => (
          <div key={s.n} className="flex items-center gap-2 flex-1">
            <button
              type="button"
              onClick={() => setStep(s.n)}
              data-testid={`wizard-step-${s.n}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all ${
                step === s.n ? "bg-primary text-primary-foreground" :
                step > s.n ? "bg-secondary text-foreground" :
                "bg-card border border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step === s.n ? "bg-primary-foreground text-primary" :
                step > s.n ? "bg-primary text-primary-foreground" :
                "bg-muted text-muted-foreground"
              }`}>
                {step > s.n ? "✓" : s.n}
              </span>
              <span className="font-medium">{s.label}</span>
            </button>
            {i < steps.length - 1 && (
              <div className={`h-px flex-1 ${step > s.n ? "bg-primary" : "bg-border"}`} />
            )}
          </div>
        ))}
      </div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8 space-y-6">
          {/* STEP 1: Dog type */}
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1-content">
              <p className="text-sm text-muted-foreground">
                Choose how you&apos;ll use this dog&apos;s profile. We&apos;ll tailor the sections and prompts to match.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {DOG_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = (profileForm.dog_type || "") === t.value;
                  return (
                    <button
                      type="button"
                      key={t.value}
                      onClick={() => setP("dog_type", t.value)}
                      data-testid={`profile-dog-type-${t.value}`}
                      className={`flex items-start gap-3 p-4 rounded-md border text-left transition-all ${
                        active ? "border-primary bg-secondary" : "border-border bg-card hover:bg-muted hover:-translate-y-0.5"
                      }`}
                    >
                      <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${
                        active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                      }`}>
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm">{t.label}</p>
                        <p className="text-[11px] text-muted-foreground leading-snug mt-1">{t.blurb}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 2: Dog details */}
          {step === 2 && (
            <div className="space-y-6" data-testid="wizard-step-2-content">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label>Photo</Label>
                  <div className="mt-1.5 aspect-square bg-muted rounded-md overflow-hidden border border-border">
                    {profileForm.photo_url ? (
                      <img src={profileForm.photo_url} alt={profileForm.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No photo</div>
                    )}
                  </div>
                  <Input type="file" accept="image/*" onChange={onPhoto} className="mt-2" data-testid="profile-photo-input" />
                </div>

                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="p-name">Name</Label>
                    <Input id="p-name" value={profileForm.name || ""} onChange={(e) => setP("name", e.target.value)} className="mt-1.5" data-testid="profile-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="p-breed">Breed</Label>
                    {(() => {
                      const value = profileForm.breed || "";
                      const isCustom = !!value && !POPULAR_BREEDS.includes(value);
                      const selectVal = isCustom ? "__custom__" : value;
                      return (
                        <>
                          <select
                            id="p-breed"
                            value={selectVal}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "__custom__") {
                                // switch to custom mode while preserving any existing custom value
                                setP("breed", isCustom ? value : "");
                              } else {
                                setP("breed", v);
                              }
                            }}
                            className="mt-1.5 w-full border border-input rounded-md bg-background px-3 py-2 text-sm h-10"
                            data-testid="profile-breed-select"
                          >
                            <option value="">Select a breed…</option>
                            {POPULAR_BREEDS.map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                            <option value="__custom__">Other (type a custom breed)…</option>
                          </select>
                          {(isCustom || selectVal === "__custom__") && (
                            <Input
                              value={value}
                              onChange={(e) => setP("breed", e.target.value)}
                              placeholder="Custom breed (e.g. Goldendoodle, mixed breed)"
                              className="mt-2"
                              data-testid="profile-breed-custom-input"
                              autoFocus={selectVal === "__custom__" && !value}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    <Label htmlFor="p-bday">Birthday / Adoption Day</Label>
                    <Input id="p-bday" type="date" value={profileForm.birthday || ""} onChange={(e) => setP("birthday", e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="p-sex">Sex</Label>
                    <Input id="p-sex" value={profileForm.sex || ""} onChange={(e) => setP("sex", e.target.value)} className="mt-1.5" placeholder="Male / Female" />
                  </div>
                  <div>
                    <Label htmlFor="p-color">Color / markings</Label>
                    <Input id="p-color" value={profileForm.color || ""} onChange={(e) => setP("color", e.target.value)} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="p-akc">AKC#, UKC#, IFC#, Other</Label>
                    <Input id="p-akc" value={profileForm.akc_number || ""} onChange={(e) => setP("akc_number", e.target.value)} className="mt-1.5" data-testid="profile-akc-input" />
                  </div>
                  <div>
                    <Label htmlFor="p-chip">Microchip #</Label>
                    <Input id="p-chip" value={profileForm.microchip || ""} onChange={(e) => setP("microchip", e.target.value)} className="mt-1.5" />
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="p-notes">Notes</Label>
                <Textarea id="p-notes" rows={4} value={profileForm.notes || ""} onChange={(e) => setP("notes", e.target.value)} className="mt-1.5" placeholder="Anything you want to remember about this dog…" />
              </div>
            </div>
          )}

          {/* STEP 3: Pedigree */}
          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-3-content">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                <div className="md:col-span-1 flex items-center">
                  <div className="w-full border-2 border-primary rounded-md p-4 bg-card">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">This dog</p>
                    <p className="font-display font-bold text-xl tracking-tight mt-1">{activeDog.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{activeDog.breed || "—"}</p>
                  </div>
                </div>
                <div className="md:col-span-1 grid grid-rows-2 gap-4">
                  <div className="border border-border rounded-md p-3 bg-card">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Sire</p>
                    <Input value={profileForm.sire || ""} onChange={(e) => setP("sire", e.target.value)} className="h-8 text-sm" data-testid="ped-sire" />
                  </div>
                  <div className="border border-border rounded-md p-3 bg-card">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Dam</p>
                    <Input value={profileForm.dam || ""} onChange={(e) => setP("dam", e.target.value)} className="h-8 text-sm" data-testid="ped-dam" />
                  </div>
                </div>
                <div className="md:col-span-1 grid grid-rows-4 gap-3">
                  {[
                    { k: "sire_sire", label: "Sire's sire", testid: "ped-sire-sire" },
                    { k: "sire_dam", label: "Sire's dam", testid: "ped-sire-dam" },
                    { k: "dam_sire", label: "Dam's sire", testid: "ped-dam-sire" },
                    { k: "dam_dam", label: "Dam's dam", testid: "ped-dam-dam" },
                  ].map((n) => (
                    <div key={n.k} className="border border-border rounded-md p-3 bg-card">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{n.label}</p>
                      <Input value={profileForm[n.k] || ""} onChange={(e) => setP(n.k, e.target.value)} className="h-8 text-sm" data-testid={n.testid} />
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Enter registered names. They&apos;ll save when you click <strong>Finish &amp; save</strong> below.</p>
            </div>
          )}

          {/* Footer nav */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              data-testid="wizard-back-button"
            >
              ← Back
            </Button>
            {step < 3 ? (
              <Button
                onClick={() => canNext && setStep((s) => Math.min(3, s + 1))}
                disabled={!canNext}
                data-testid="wizard-next-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Next →
              </Button>
            ) : (
              <Button
                onClick={saveProfile}
                disabled={savingProfile}
                data-testid="save-profile-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {savingProfile ? "Saving…" : "Finish & save"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Rainbow Bridge memorial strip — soft in-memory card shown right under the
// hero when a dog is marked as at the Rainbow Bridge. Owners can inline-edit
// the favorite-memory field so it grows over time.
// ---------------------------------------------------------------------------
function RainbowBridgeStrip({ dog, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [memorial, setMemorial] = useState(dog.memorial || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setMemorial(dog.memorial || ""); }, [dog.id, dog.memorial]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...dog };
      delete payload.id; delete payload.owner_id; delete payload.shared;
      delete payload.share_permission; delete payload.created_at; delete payload.updated_at;
      payload.memorial = memorial.trim();
      await api.put(`/dogs/${dog.id}`, payload);
      toast.success("Memory saved");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const passingText = dog.passed_on
    ? `Passed ${format(parseISO(dog.passed_on), "MMMM d, yyyy")}`
    : "In loving memory";

  return (
    <Card
      className="border-primary/20 shadow-none rounded-lg bg-gradient-to-br from-secondary/50 to-accent/20"
      data-testid="rainbow-bridge-strip"
    >
      <CardContent className="p-6 sm:p-7">
        <div className="flex items-start gap-4">
          {dog.memorial_photo_url ? (
            <div className="shrink-0 relative" data-testid="rainbow-bridge-photo">
              <img
                src={dog.memorial_photo_url}
                alt={`In memory of ${dog.name}`}
                className="h-24 w-24 sm:h-28 sm:w-28 object-cover rounded-lg border-2 border-primary/25 shadow-sm"
              />
              {/* Small heart badge overlays the photo so the tribute reads at a glance */}
              <div className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow">
                <Heart size={12} fill="currentColor" strokeWidth={0} />
              </div>
            </div>
          ) : (
            <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Heart size={20} fill="currentColor" strokeWidth={0} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Rainbow Bridge</p>
            <p className="font-display text-xl font-bold tracking-tight mt-1">
              {dog.name}
            </p>
            <p className="text-sm text-muted-foreground mt-0.5" data-testid="rainbow-bridge-passing-text">
              {passingText}
            </p>

            {editing ? (
              <div className="mt-4 space-y-2">
                <Textarea
                  rows={3}
                  value={memorial}
                  onChange={(e) => setMemorial(e.target.value)}
                  placeholder="A favorite memory — zoomies on first snow, the way he'd sigh dramatically before bed, anything you want to remember."
                  maxLength={500}
                  data-testid="rainbow-bridge-memorial-input"
                />
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setEditing(false); setMemorial(dog.memorial || ""); }}
                    data-testid="rainbow-bridge-memorial-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="rainbow-bridge-memorial-save"
                  >
                    {saving ? "Saving…" : "Save memory"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {dog.memorial ? (
                  <p
                    className="mt-4 text-sm text-foreground/85 leading-relaxed whitespace-pre-line font-serif italic"
                    data-testid="rainbow-bridge-memorial-text"
                  >
                    {dog.memorial}
                  </p>
                ) : (
                  <p className="mt-4 text-sm text-muted-foreground italic">
                    Add a favorite memory to keep here.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="mt-3 text-xs font-medium text-primary hover:underline"
                  data-testid="rainbow-bridge-memorial-edit"
                >
                  {dog.memorial ? "Edit memory" : "Add a memory"}
                </button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
